import * as Headless from '@headlessui/react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import type { PresentationCv } from '../../../../../js/api';
import { Button } from '../../../components/button';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/dropdown';
import { Spinner } from '../../../components/spinner';
import {
  downloadPresentationPackage,
  downloadPresentationPdf,
} from '../../../utils/downloadConnectionPresentation';

type PresentationDownloadsProps = {
  connectionId: string;
  cv: PresentationCv;
  /** Download the CV document (the documents framework's own download). */
  onDownloadCv: () => void;
};

/**
 * "Download" on the Presentation Info tab: the presentation PDF, the CV, or both in
 * one zip — what "presentation" to other systems sends on before credentialing.
 * Nothing is stored; the PDF is generated from the tab's own payload on request.
 */
export function PresentationDownloads({
  connectionId,
  cv,
  onDownloadCv,
}: PresentationDownloadsProps) {
  const [isBusy, setIsBusy] = useState(false);
  const hasCv = !!cv.document || !!cv.legacy_url;

  const run = async (download: () => Promise<string>) => {
    setIsBusy(true);
    try {
      toast.success(`Saved ${await download()}`);
    } catch {
      toast.error("Couldn't prepare the download — please try again.");
    } finally {
      setIsBusy(false);
    }
  };

  const downloadPdf = () => run(() => downloadPresentationPdf(connectionId));
  const downloadPackage = () =>
    run(async () => {
      const { filename, cvIncluded } = await downloadPresentationPackage(connectionId);
      if (!cvIncluded) toast.warn('No CV on file — the zip holds the presentation only.');
      return filename;
    });
  const downloadCv = () => {
    if (cv.document) {
      onDownloadCv();
      return;
    }
    if (cv.legacy_url) window.open(cv.legacy_url, '_blank', 'noopener');
  };

  return (
    <Dropdown>
      <DropdownButton
        as={Button}
        color="brand-green-outline"
        small
        disabled={isBusy}
        data-testid="presentation-download-button"
      >
        {isBusy ? (
          <Spinner size="sm" variant="zinc" aria-label="Preparing the download" />
        ) : (
          <i className="far fa-download" />
        )}
        Download
      </DropdownButton>
      <Headless.Portal>
        <DropdownMenu anchor="bottom start" className="tw:z-[70]">
          <DropdownItem onClick={downloadPdf} data-testid="presentation-download-pdf">
            <i data-slot="icon" className="far fa-fw fa-file-pdf" />
            Presentation (PDF)
          </DropdownItem>
          <DropdownItem
            onClick={downloadCv}
            disabled={!hasCv}
            data-testid="presentation-download-cv"
          >
            <i data-slot="icon" className="far fa-fw fa-file-lines" />
            CV
          </DropdownItem>
          <DropdownItem onClick={downloadPackage} data-testid="presentation-download-package">
            <i data-slot="icon" className="far fa-fw fa-file-zipper" />
            Both (zip)
          </DropdownItem>
        </DropdownMenu>
      </Headless.Portal>
    </Dropdown>
  );
}
